import { FETCH_TIMEOUT_MS, MAX_PDF_FETCH_RETRIES, PDF_MARGIN_DEFAULT } from "@client-shared/lib/constants";
import { SimplePdfMerger } from "@client-shared/lib/pdfMerger";
import { fitPdfInMemory, serializedCopyPages as globalSerializedCopyPages } from "@client-shared/lib/pdfPageFitter";
import type { PaperSize } from "@client-shared/types/pdfPrint";
import { PDFDocument, PDFPage } from "pdf-lib";
import { extractErrorMessage } from "./errorUtils";

/**
 * PDF processing utilities for ShippingDocumentsDialog
 */

/**
 * Creates a serialized addPage function that queues page additions to prevent race conditions.
 * This allows PDFs to be processed incrementally (to avoid timeouts) while ensuring
 * pages are added to the merged PDF in a thread-safe, ordered manner.
 *
 * @param mergedPdf - The PDF document to add pages to
 * @returns A function that queues and executes addPage operations sequentially
 */
export const createSerializedAddPage = (mergedPdf: PDFDocument): ((page: PDFPage) => Promise<void>) => {
  let addPageQueue: Promise<void> = Promise.resolve();

  return async (page: PDFPage): Promise<void> => {
    // Chain the addPage operation to the queue to ensure sequential execution
    const currentPromise = addPageQueue.then(() => {
      mergedPdf.addPage(page);
    });
    // Ensure the queue continues even if this operation fails
    // This prevents one error from breaking subsequent operations
    addPageQueue = currentPromise.catch(() => {
      // Continue the chain even if this operation failed
    });
    // Propagate error to caller while maintaining queue integrity
    await currentPromise;
  };
};

/** Fits a PDF to paper in memory, optionally stamping a PO number bar along the bottom. */
export type FitPdfInMemoryFn = (srcDoc: PDFDocument, paperSize: PaperSize, margin: number, poNumber?: string) => Promise<PDFDocument>;

/**
 * Creates a serialized fitPdfInMemory function that queues fitPdfInMemory operations to prevent race conditions.
 *
 * IMPORTANT: pdf-lib's PDFDocument operations are not safe for concurrent calls. While the streaming feature
 * (commit bf3f3c1d8) worked initially, recent browser updates (2024-2025) have exposed race conditions that
 * cause internal state corruption, resulting in NaN errors. Serializing all pdf-lib operations prevents this.
 *
 * @returns A function that queues and executes fitPdfInMemory operations sequentially
 */
export const createSerializedFitPdfInMemory = (): FitPdfInMemoryFn => {
  let fitPdfQueue: Promise<void> = Promise.resolve();

  return async (srcDoc: PDFDocument, paperSize: PaperSize, margin: number, poNumber?: string): Promise<PDFDocument> => {
    let result: PDFDocument | undefined;
    // Chain the fitPdfInMemory operation to the queue to ensure sequential execution
    const currentPromise = fitPdfQueue.then(async () => {
      result = await fitPdfInMemory(srcDoc, paperSize, margin, poNumber);
    });
    // Ensure the queue continues even if this operation fails
    // This prevents one error from breaking subsequent operations
    fitPdfQueue = currentPromise.catch(() => {
      // Continue the chain even if this operation failed
    });
    // Propagate error to caller while maintaining queue integrity
    await currentPromise;
    if (!result) {
      throw new Error("fitPdfInMemory returned undefined result");
    }
    return result;
  };
};

/**
 * Checks if operation is aborted
 */
export const isAborted = (signal?: AbortSignal, error?: unknown): boolean => {
  if (signal?.aborted) {
    return true;
  }
  if (error instanceof Error && error.name === "AbortError") {
    return true;
  }
  return false;
};

/**
 * Adds pages to merged PDF with abort checking
 */
const addPagesWithAbortCheck = async (
  pages: PDFPage[],
  serializedAddPage: (page: PDFPage) => Promise<void>,
  signal?: AbortSignal
): Promise<boolean> => {
  for (const page of pages) {
    if (signal?.aborted) {
      return false;
    }
    // Pages are appended to the merged document in order.
    await serializedAddPage(page); // NOSONAR
  }
  return true;
};

/**
 * Validates that a value is a valid PDFDocument instance.
 * Throws a descriptive error if validation fails.
 */
const validatePdfDocument = (pdf: unknown, context: string): PDFDocument => {
  if (pdf === null || typeof pdf !== "object") {
    throw new TypeError(`${context}: Expected PDFDocument but got ${typeof pdf} (${String(pdf)})`);
  }
  if (typeof (pdf as PDFDocument).getPageIndices !== "function") {
    throw new TypeError(`${context}: PDFDocument missing getPageIndices method`);
  }
  // Check for NaN by attempting to get page indices
  try {
    const indices = (pdf as PDFDocument).getPageIndices();
    if (!Array.isArray(indices)) {
      throw new TypeError(`${context}: getPageIndices() did not return an array`);
    }
    for (const idx of indices) {
      if (typeof idx !== "number" || !Number.isFinite(idx) || idx < 0) {
        throw new TypeError(`${context}: Invalid page index in getPageIndices(): ${idx}`);
      }
    }
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("Invalid page index")) {
      throw error;
    }
    throw new TypeError(`${context}: Failed to validate PDFDocument: ${error instanceof Error ? error.message : String(error)}`);
  }
  return pdf as PDFDocument;
};

/**
 * Validates page indices array for a fitted label PDF.
 * Returns false if validation fails, true otherwise.
 */
const validatePageIndices = (pageIndices: number[], url: string): boolean => {
  if (!Array.isArray(pageIndices) || pageIndices.length === 0) {
    console.warn(`Invalid page indices for fitted label from ${url}: ${JSON.stringify(pageIndices)}`);
    return false;
  }

  for (const idx of pageIndices) {
    if (typeof idx !== "number" || !Number.isFinite(idx) || idx < 0) {
      console.warn(`Invalid page index in fitted label from ${url}: ${idx}`);
      return false;
    }
  }

  return true;
};

/**
 * Validates PDF documents before copyPages operation.
 * Returns false if validation fails, true otherwise.
 */
const validateBeforeCopyPages = (mergedPdf: PDFDocument, validatedFittedLabel: PDFDocument, url: string): boolean => {
  if (!mergedPdf || typeof mergedPdf !== "object" || typeof mergedPdf.copyPages !== "function") {
    console.warn(`mergedPdf is invalid when trying to copy pages from ${url}`);
    return false;
  }

  if (!validatedFittedLabel || typeof validatedFittedLabel !== "object") {
    console.warn(`validatedFittedLabel became invalid before copyPages for ${url}: ${typeof validatedFittedLabel}`);
    return false;
  }

  if (typeof validatedFittedLabel.getPageIndices !== "function") {
    console.warn(`validatedFittedLabel missing getPageIndices before copyPages for ${url}`);
    return false;
  }

  return true;
};

/**
 * Copies pages from fitted label to merged PDF with error handling.
 * Returns null if NaN error (should skip label), throws for other errors.
 */
const copyPagesWithErrorHandling = async (
  mergedPdf: PDFDocument,
  validatedFittedLabel: PDFDocument,
  pageIndices: number[],
  url: string
): Promise<PDFPage[] | null> => {
  try {
    // Use global serialized copyPages to prevent race conditions with concurrent processing
    // This uses the same global queue as fitPdfCore, ensuring all copyPages calls are truly serialized
    // pdf-lib's copyPages may not be safe for concurrent calls, even on different PDFDocuments
    return await globalSerializedCopyPages(mergedPdf, validatedFittedLabel, pageIndices);
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);

    // Handle NaN errors - these indicate pdf-lib internal corruption
    if (errorMsg.includes("NaN") || errorMsg.includes("type NaN")) {
      console.warn(
        `Failed to copy pages from fitted label ${url} due to NaN error (pdf-lib bug). Skipping this label and continuing with others.`
      );
      // For NaN errors, return null to skip this label gracefully
      return null;
    }

    // Re-throw with more context for other errors
    console.warn(`Failed to copy pages from fitted label ${url}: ${errorMsg}`);
    throw new Error(`Failed to copy pages from fitted label: ${errorMsg}`);
  }
};

/**
 * Fetches and processes label PDF
 */
interface FetchAndProcessLabelOptions {
  url: string;
  mergedPdf: PDFDocument;
  sizeToUse: PaperSize;
  serializedAddPage: (page: PDFPage) => Promise<void>;
  serializedFitPdfInMemory: FitPdfInMemoryFn;
  signal?: AbortSignal;
  poNumber?: string;
}

const fetchAndProcessLabel = async (options: FetchAndProcessLabelOptions): Promise<boolean> => {
  const { url, mergedPdf, sizeToUse, serializedAddPage, serializedFitPdfInMemory, signal, poNumber } = options;

  const srcPdf = await SimplePdfMerger.fetchPdfWithRetry(url, {
    timeout: FETCH_TIMEOUT_MS,
    maxRetries: MAX_PDF_FETCH_RETRIES,
    signal,
  });

  if (isAborted(signal) || !srcPdf) {
    return false;
  }

  // Validate srcPdf before processing
  // Note: We rely on validation in fetchAndLoadPdf (pdfMerger.ts) which already tests the PDFDocument
  // with a test copy immediately after loading. If that validation passes, the PDF should be valid here.
  const validatedSrcPdf = validatePdfDocument(srcPdf, `Label PDF from ${url}`);

  let fittedLabel: PDFDocument;
  try {
    // Serialized: pdf-lib's PDFDocument operations are not safe for concurrent calls.
    fittedLabel = await serializedFitPdfInMemory(validatedSrcPdf, sizeToUse, PDF_MARGIN_DEFAULT, poNumber);
  } catch (error) {
    // If fitPdfInMemory fails (e.g., due to corrupted PDF), log and skip this label
    console.warn(`Failed to fit PDF from ${url}: ${error instanceof Error ? error.message : String(error)}`);
    return false;
  }

  if (isAborted(signal)) {
    return false;
  }

  // Validate fittedLabel before copying pages - check for NaN or invalid values
  if (!fittedLabel || (typeof fittedLabel === "number" && !Number.isFinite(fittedLabel))) {
    console.warn(`fitPdfInMemory returned invalid value for ${url}: ${typeof fittedLabel} (${String(fittedLabel)})`);
    return false;
  }

  const validatedFittedLabel = validatePdfDocument(fittedLabel, `Fitted label PDF from ${url}`);

  // Basic validation: ensure the fitted label has valid structure
  // Note: We rely on fitPdfInMemory's internal validation which already tests the PDFDocument
  // The test copy was causing issues because it also calls pdf-lib operations that may need serialization
  const pageIndices = validatedFittedLabel.getPageIndices();

  if (!validatePageIndices(pageIndices, url)) {
    return false;
  }

  if (!validateBeforeCopyPages(mergedPdf, validatedFittedLabel, url)) {
    return false;
  }

  const labelPages = await copyPagesWithErrorHandling(mergedPdf, validatedFittedLabel, pageIndices, url);
  if (!labelPages) {
    return false; // NaN error - skip this label
  }
  return await addPagesWithAbortCheck(labelPages, serializedAddPage, signal);
};

/**
 * Processes packing slip if available
 */
const processPackingSlip = async (
  slipMap: Map<number, PDFDocument>,
  orderIndex: number | undefined,
  mergedPdf: PDFDocument,
  serializedAddPage: (page: PDFPage) => Promise<void>,
  signal?: AbortSignal
): Promise<boolean> => {
  if (orderIndex === undefined || isAborted(signal)) {
    return false;
  }

  const slip = slipMap.get(orderIndex);
  if (!slip) {
    return true;
  } // Not an error if slip doesn't exist

  // Validate slip before copying pages
  const validatedSlip = validatePdfDocument(slip, `Packing slip for order index ${orderIndex}`);

  const pageIndices = validatedSlip.getPageIndices();

  // Use global serialized copyPages to prevent race conditions
  // This uses the same global queue as fitPdfCore, ensuring all copyPages calls are truly serialized
  const slipPages = await globalSerializedCopyPages(mergedPdf, validatedSlip, pageIndices);

  return await addPagesWithAbortCheck(slipPages, serializedAddPage, signal);
};

/**
 * Options for processing label and slip for combined PDF.
 */
interface ProcessLabelAndSlipOptions {
  mergedPdf: PDFDocument;
  slipMap: Map<number, PDFDocument>;
  sizeToUse: PaperSize;
  orderIndex: number | undefined;
  serializedAddPage: (page: PDFPage) => Promise<void>;
  serializedFitPdfInMemory?: FitPdfInMemoryFn;
  signal?: AbortSignal;
  poNumber?: string;
}

/**
 * Processes a label and its corresponding slip for combined PDF.
 * Uses serialized addPage, copyPages, and fitPdfInMemory to prevent race conditions while maintaining incremental processing.
 */
export const processLabelAndSlipForCombined = async (url: string, options: ProcessLabelAndSlipOptions): Promise<void> => {
  const { mergedPdf, slipMap, sizeToUse, orderIndex, serializedAddPage, serializedFitPdfInMemory, signal, poNumber } = options;

  if (isAborted(signal)) {
    return;
  }

  // Use provided serializedFitPdfInMemory if available, otherwise create a new one
  // This allows sharing a single serialized instance across all concurrent calls
  const fitPdfInMemoryFn = serializedFitPdfInMemory ?? createSerializedFitPdfInMemory();

  try {
    const labelProcessed = await fetchAndProcessLabel({
      url,
      mergedPdf,
      sizeToUse,
      serializedAddPage,
      serializedFitPdfInMemory: fitPdfInMemoryFn,
      signal,
      poNumber,
    });
    if (!labelProcessed) {
      return;
    }

    await processPackingSlip(slipMap, orderIndex, mergedPdf, serializedAddPage, signal);
  } catch (error) {
    if (isAborted(signal, error)) {
      return;
    }

    const errorMessage = extractErrorMessage(error);
    // Include more context about which PDF failed (label vs slip)
    const errorDetails = error instanceof Error ? error.message : String(error);
    console.error(
      `Error processing label/slip for order ${orderIndex ?? "unknown"}: ${errorMessage}`,
      errorDetails.includes("PDFDocument") || errorDetails.includes("NaN") ? `\n  Details: ${errorDetails}` : ""
    );
  }
};
